import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Calendar,
  ChevronRight,
  Clock,
  Facebook,
  Info,
  Linkedin,
  Link2,
  Mail,
  Tag,
  Twitter,
  User,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { CAPE_VERDE_POST as POST, RELATED_POSTS } from "../data/blog";
import { Reveal, Eyebrow } from "../components/common";
import { useToast } from "../hooks/use-toast";

export default function BlogPost() {
  const [progress, setProgress] = useState(0);
  const [email, setEmail] = useState("");
  const { toast } = useToast();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement;
      const max = h.scrollHeight - h.clientHeight;
      setProgress(max > 0 ? Math.min((h.scrollTop / max) * 100, 100) : 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const copyLink = () => {
    const url = window.location.href;
    if (navigator.clipboard) navigator.clipboard.writeText(url);
    toast({ title: "Link copied", description: "Share it with a future volunteer." });
  };

  const subscribe = (e) => {
    e.preventDefault();
    if (!email.includes("@")) {
      toast({ title: "Check your email", description: "That address doesn't look right." });
      return;
    }
    toast({ title: "You're subscribed!", description: `Monthly IVI stories heading to ${email}.` });
    setEmail("");
  };

  return (
    <div className="bg-white">
      <Navbar />

      {/* Reading progress */}
      <div className="fixed left-0 top-[74px] z-40 h-1 w-full bg-ivi-mint/60">
        <div
          className="h-full bg-ivi-green transition-[width] duration-150"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Hero */}
      <header className="relative isolate overflow-hidden bg-ivi-slate pb-16 pt-32 lg:pb-20 lg:pt-40">
        <img
          src={POST.hero}
          alt="Cape Verde coastline"
          className="absolute inset-0 -z-10 h-full w-full object-cover"
        />
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-ivi-slate/85 via-ivi-slate/70 to-ivi-slate/90" />
        <div className="grain absolute inset-0 -z-10" />

        <div className="mx-auto max-w-[1400px] px-5 lg:px-8">
          <nav className="flex flex-wrap items-center gap-2 text-[13px] font-semibold text-white/65">
            <Link to="/" className="transition-colors duration-200 hover:text-white">
              Home
            </Link>
            <ChevronRight className="h-3.5 w-3.5" />
            <Link to="/sitemap" className="transition-colors duration-200 hover:text-white">
              Blog
            </Link>
            <ChevronRight className="h-3.5 w-3.5" />
            <span className="text-ivi-lime">{POST.title}</span>
          </nav>

          <div className="mt-7 max-w-3xl">
            <span className="inline-flex items-center gap-2 rounded-full bg-ivi-lime px-4 py-1.5 font-heading text-[12px] font-extrabold uppercase tracking-[0.16em] text-ivi-slate">
              <Tag className="h-3.5 w-3.5" /> {POST.category}
            </span>
            <h1 className="mt-6 font-heading text-[38px] font-extrabold leading-[1.06] tracking-[-0.02em] text-white sm:text-[54px]">
              {POST.title}
            </h1>
            <p className="mt-6 text-[18px] leading-relaxed text-white/85">{POST.standfirst}</p>

            <div className="mt-8 flex flex-wrap items-center gap-x-7 gap-y-3 text-[14px] font-semibold text-white/75">
              <span className="flex items-center gap-2">
                <span className="grid h-9 w-9 place-items-center rounded-full bg-white/15 text-ivi-lime">
                  <User className="h-4 w-4" />
                </span>
                {POST.author}
                <span className="font-normal text-white/50">· {POST.role}</span>
              </span>
              <span className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-ivi-lime" /> {POST.date}
              </span>
              <span className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-ivi-lime" /> {POST.readTime}
              </span>
            </div>
          </div>
        </div>
      </header>

      {/* Body + sidebar */}
      <div className="mx-auto max-w-[1400px] px-5 py-16 lg:px-8 lg:py-24">
        <div className="grid gap-12 lg:grid-cols-[minmax(0,1fr)_360px] xl:gap-16">
          {/* Article */}
          <article className="min-w-0">
            {/* Fast facts */}
            <Reveal>
              <div className="rounded-3xl bg-ivi-mint p-7 sm:p-9">
                <p className="flex items-center gap-2 font-heading text-[13px] font-bold uppercase tracking-[0.16em] text-ivi-greenDark">
                  <Info className="h-4 w-4" /> General facts
                </p>
                <dl className="mt-6 grid gap-x-8 gap-y-4 sm:grid-cols-2">
                  {POST.facts.map((f) => (
                    <div key={f.k} className="border-b border-white pb-3">
                      <dt className="text-[12px] font-bold uppercase tracking-[0.12em] text-ivi-slateSoft">
                        {f.k}
                      </dt>
                      <dd className="mt-1 font-heading text-[15px] font-bold text-ivi-slate">
                        {f.v}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            </Reveal>

            {POST.sections.map((s) => (
              <section key={s.id} id={s.id} className="scroll-mt-32 pt-14">
                <h2 className="font-heading text-[28px] font-extrabold leading-tight tracking-[-0.01em] text-ivi-slate sm:text-[34px]">
                  {s.heading}
                </h2>
                <div className="mt-5 space-y-5">
                  {s.paras.map((p, i) => (
                    <p
                      key={i}
                      className={`text-[17px] leading-[1.75] text-ivi-slate/85 ${
                        i === 0
                          ? "first-letter:float-left first-letter:mr-3 first-letter:font-heading first-letter:text-[54px] first-letter:font-extrabold first-letter:leading-[0.8] first-letter:text-ivi-greenDark"
                          : ""
                      }`}
                    >
                      {p}
                    </p>
                  ))}
                </div>

                {s.images.length > 0 && (
                  <div
                    className={`mt-8 grid gap-4 ${
                      s.images.length > 1 ? "sm:grid-cols-2" : ""
                    }`}
                  >
                    {s.images.map((im) => (
                      <Reveal key={im.src}>
                        <figure className="group overflow-hidden rounded-3xl">
                          <img
                            src={im.src}
                            alt={im.caption}
                            loading="lazy"
                            className="h-64 w-full object-cover transition-transform duration-[1000ms] group-hover:scale-105"
                          />
                          <figcaption className="mt-2 text-[13px] font-semibold text-ivi-slateSoft">
                            {im.caption}
                          </figcaption>
                        </figure>
                      </Reveal>
                    ))}
                  </div>
                )}
              </section>
            ))}

            {/* Closing pull quote */}
            <Reveal className="mt-16">
              <blockquote className="rounded-3xl border-l-4 border-ivi-green bg-ivi-sand p-8 sm:p-10">
                <p className="font-heading text-[20px] font-bold leading-relaxed text-ivi-slate sm:text-[23px]">
                  {POST.closing}
                </p>
              </blockquote>
            </Reveal>

            {/* Tags + share */}
            <div className="mt-12 flex flex-wrap items-center justify-between gap-6 border-t border-ivi-mint pt-8">
              <div className="flex flex-wrap gap-2">
                {POST.tags.map((t) => (
                  <Link
                    key={t}
                    to="/sitemap"
                    className="rounded-full bg-ivi-mint px-4 py-2 text-[13px] font-semibold text-ivi-greenDark transition-colors duration-200 hover:bg-ivi-green hover:text-white"
                  >
                    #{t}
                  </Link>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <span className="mr-1 text-[13px] font-bold uppercase tracking-[0.14em] text-ivi-slateSoft">
                  Share
                </span>
                {[Facebook, Twitter, Linkedin, Mail].map((I, i) => (
                  <a
                    key={i}
                    href="#share"
                    aria-label="Share this article"
                    className="grid h-10 w-10 place-items-center rounded-full bg-ivi-sand text-ivi-slate transition-colors duration-200 hover:bg-ivi-green hover:text-white"
                  >
                    <I className="h-[17px] w-[17px]" />
                  </a>
                ))}
                <button
                  type="button"
                  onClick={copyLink}
                  aria-label="Copy link"
                  className="grid h-10 w-10 place-items-center rounded-full bg-ivi-sand text-ivi-slate transition-colors duration-200 hover:bg-ivi-green hover:text-white"
                >
                  <Link2 className="h-[17px] w-[17px]" />
                </button>
              </div>
            </div>

            {/* Author card */}
            <Reveal className="mt-10">
              <div className="flex flex-wrap items-center gap-6 rounded-3xl bg-ivi-slate p-8 text-white">
                <span className="grid h-16 w-16 shrink-0 place-items-center rounded-full bg-ivi-green font-heading text-[22px] font-extrabold">
                  ST
                </span>
                <div className="min-w-0 flex-1">
                  <p className="font-heading text-[18px] font-extrabold">{POST.author}</p>
                  <p className="mt-1 text-[14px] text-white/70">
                    {POST.role} · Writes about new destinations, ethical volunteering and life on
                    project.
                  </p>
                </div>
                <Link
                  to="/sitemap"
                  className="inline-flex items-center gap-2 rounded-full bg-white/10 px-5 py-3 font-heading text-[14px] font-bold text-white transition-colors duration-200 hover:bg-ivi-green"
                >
                  More posts <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </Reveal>
          </article>

          {/* Sidebar */}
          <aside className="lg:sticky lg:top-28 lg:self-start">
            <div className="space-y-6">
              {/* Related posts */}
              <div className="rounded-3xl bg-ivi-sand p-6">
                <p className="font-heading text-[12px] font-bold uppercase tracking-[0.16em] text-ivi-slateSoft">
                  Keep reading
                </p>
                <ul className="mt-5 space-y-4">
                  {RELATED_POSTS.map((r) => (
                    <li key={r.path}>
                      <Link to={r.path} className="group flex gap-4">
                        <span className="h-[68px] w-[92px] shrink-0 overflow-hidden rounded-xl">
                          <img
                            src={r.img}
                            alt={r.title}
                            loading="lazy"
                            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                          />
                        </span>
                        <span className="min-w-0">
                          <span className="block text-[11px] font-bold uppercase tracking-[0.12em] text-ivi-greenDark">
                            {r.cat}
                          </span>
                          <span className="mt-1 block font-heading text-[14px] font-bold leading-snug text-ivi-slate transition-colors duration-200 group-hover:text-ivi-greenDark">
                            {r.title}
                          </span>
                          <span className="mt-1 block text-[12px] text-ivi-slateSoft">{r.date}</span>
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/sitemap"
                  className="mt-6 inline-flex items-center gap-2 font-heading text-[14px] font-bold text-ivi-greenDark"
                >
                  Browse all articles <ArrowRight className="h-4 w-4" />
                </Link>
              </div>

              {/* CTA */}
              <div className="overflow-hidden rounded-3xl bg-ivi-slate p-7 text-white">
                <p className="font-heading text-[13px] font-bold uppercase tracking-[0.16em] text-ivi-lime">
                  New projects
                </p>
                <p className="mt-3 font-heading text-[21px] font-extrabold leading-snug">
                  Volunteer in Cape Verde
                </p>
                <p className="mt-3 text-[14px] leading-relaxed text-white/75">
                  Marine conservation, teaching and community work across ten Atlantic islands. From
                  $540 USD.
                </p>
                <Link
                  to="/projects/volunteer-in-bali"
                  className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-ivi-green px-6 py-3.5 font-heading text-[14px] font-bold text-white transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-ivi-greenDark"
                >
                  See the projects <ArrowRight className="h-4 w-4" />
                </Link>
              </div>

              {/* Newsletter */}
              <div className="rounded-3xl bg-ivi-mint p-7">
                <Eyebrow>Newsletter</Eyebrow>
                <p className="mt-4 font-heading text-[18px] font-extrabold leading-snug text-ivi-slate">
                  One email a month. Real stories, new destinations.
                </p>
                <form onSubmit={subscribe} className="mt-5 space-y-3">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Your email address"
                    className="h-12 w-full rounded-full border border-white bg-white px-5 text-[14px] text-ivi-slate outline-none transition-[border-color,box-shadow] duration-200 focus:border-ivi-green focus:ring-2 focus:ring-ivi-green/25"
                  />
                  <button
                    type="submit"
                    className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-ivi-slate px-6 py-3.5 font-heading text-[14px] font-bold text-white transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-ivi-slate/90"
                  >
                    Subscribe
                  </button>
                </form>
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* Next reads */}
      <section className="bg-ivi-sand py-20">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-8">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <h2 className="font-heading text-[28px] font-extrabold text-ivi-slate sm:text-[36px]">
              More from the IVI blog
            </h2>
            <Link
              to="/sitemap"
              className="inline-flex items-center gap-2 font-heading text-[14px] font-bold text-ivi-greenDark"
            >
              View all 431 articles <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {RELATED_POSTS.slice(0, 3).map((r, i) => (
              <Reveal key={r.path} delay={i * 80}>
                <Link
                  to={r.path}
                  className="group flex h-full flex-col overflow-hidden rounded-3xl bg-white shadow-[0_14px_38px_-28px_rgba(24,40,32,0.5)] transition-[transform,box-shadow] duration-300 hover:-translate-y-1.5 hover:shadow-[0_30px_58px_-30px_rgba(24,40,32,0.5)]"
                >
                  <span className="relative block h-48 overflow-hidden">
                    <img
                      src={r.img}
                      alt={r.title}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-[1000ms] group-hover:scale-110"
                    />
                    <span className="absolute left-4 top-4 rounded-full bg-white/92 px-3 py-1 font-heading text-[11px] font-bold uppercase tracking-[0.12em] text-ivi-greenDark">
                      {r.cat}
                    </span>
                  </span>
                  <span className="flex flex-1 flex-col p-6">
                    <span className="text-[12px] font-semibold uppercase tracking-[0.12em] text-ivi-slateSoft">
                      {r.date}
                    </span>
                    <span className="mt-2 flex-1 font-heading text-[18px] font-bold leading-snug text-ivi-slate transition-colors duration-200 group-hover:text-ivi-greenDark">
                      {r.title}
                    </span>
                    <span className="mt-4 inline-flex items-center gap-2 font-heading text-[13px] font-bold text-ivi-greenDark">
                      Read more <ArrowRight className="h-3.5 w-3.5" />
                    </span>
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
